/**
 * C-01 «Список чатов», C-03 «Архив», D-01 «Двухпанельный режим».
 *
 * ПС §11: аватар, имя, превью последнего события, время, непрочитанный
 * счётчик, mute/архивный статус. ПС §17: на desktop — двухпанельный режим.
 *
 * Поиск по списку — КЛИЕНТСКИЙ (решение D-30, GAP-ALL-01): списки диалогов
 * загружаются целиком (CC §4.3 шаг 5), серверной операции поиска по диалогам нет.
 * Вторая секция того же поля — глобальный поиск людей по никнейму (C-28, BS §11.3):
 * `GET /users/search` от 5 символов, как в Telegram.
 * Поле поиска видно всегда — как в Telegram; `/` и `Ctrl/Cmd + K` ставят
 * в него фокус (D-02).
 */
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { lazy, useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react'

import { faceOf, spaceIcon } from '@entities/conversation/display'
import { useConversation, useConversations } from '@entities/conversation/model'
import { useUsernameSearch } from '@entities/user/username-search'
import { useMatchesWithoutMessages } from '@entities/match/model'
import { useNotifications, useUnreadNotificationsCount } from '@entities/notification/model'
import { useChatActions } from '@features/chat/use-chat-actions'
import { ensureSavedConversation } from '@features/chat/ensure-saved-conversation'
import { reorderPinnedConversations } from '@features/chat/pinned-order'
import { inChatFolder, useChatFolders } from '@features/chat/use-chat-folders'
import { useMessageRequests } from '@features/chat/use-message-requests'
import type { Conversation, UserSearchResult } from '@shared/api/types'
import { conversationsRepo, draftsRepo, useReplicaList } from '@shared/db'
import { useIsDesktopLayout } from '@shared/lib/use-media-query'
import { usernameRules, usernameSearchQuery } from '@shared/lib/username'
import {
  ActionItem,
  ActionList,
  Avatar,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Icon,
  LogoTile,
  SearchInput,
  Sheet,
  VIRTUALIZE_THRESHOLD,
  VirtualList,
  cx,
  useToast,
} from '@shared/ui'

import { CHAT_SEARCH_ATTR, KEY_ITEM_ATTR, KEY_LIST_ATTR } from '../../model/hotkeys'
import { Screen } from '../../layouts/Screen'
import { useAppStore } from '../../model/app-store'
import { useTyping } from '../../model/typing-store'
import { setVisibleConversations } from '../../boot/visible-conversations'
import { formatListTime } from '../../lib/format'
import { NotificationsScreen, describeNotification } from '../notifications/NotificationsScreen'
import { ChatScreen } from './ChatScreen'
import { TopicListScreen } from './TopicListScreen'
import { MessageReceipt } from './MessageReceipt'
import { MUTE_OPTIONS } from './mute-options'
import { SwipeableRow, type SwipeAction } from './SwipeableRow'
import styles from './chats.module.css'
import roadmap from './roadmap.module.css'

const NewMessageSheet = lazy(async () => ({
  default: (await import('./NewMessageSheet')).NewMessageSheet,
}))

/** Совпадает с `--conversation-row-height` (§3.3): оценка до измерения. */
const CONVERSATION_ROW_HEIGHT = 72

/** Идентификатор служебного диалога «Движ» в списке — вместо id переписки. */
const SERVICE_ROW_ID = '__notifications'

export function ChatsScreen({
  archive = false,
  notifications = false,
}: {
  archive?: boolean
  /** Открыт служебный диалог «Движ» (центр уведомлений N-01). */
  notifications?: boolean
}): React.ReactElement {
  const isDesktop = useIsDesktopLayout()
  const params = useParams({ strict: false }) as { conversationId?: string }
  const conversationId = params.conversationId ?? null

  // §3.5: ниже 1024 px двухпанельный режим не сжимается, а заменяется
  // одноколоночным — открытый диалог занимает экран целиком.
  if (!isDesktop) {
    if (notifications) return <NotificationsScreen />
    return conversationId ? (
      <ChatOrTopics conversationId={conversationId} />
    ) : (
      <ConversationListPane archive={archive} />
    )
  }

  return <SplitLayout archive={archive} notifications={notifications} />
}

/* ===================================================================== */
/* D-01: список + открытый диалог                                         */
/* ===================================================================== */

function SplitLayout({ archive, notifications }: { archive: boolean; notifications: boolean }) {
  // Маршрут диалога живёт в том же layout, поэтому id читаем мягко.
  const params = useParams({ strict: false }) as { conversationId?: string }
  const conversationId = params.conversationId ?? null

  return (
    <div className={styles['split']}>
      <div className={styles['listPane']}>
        <ConversationListPane archive={archive} activeId={notifications ? SERVICE_ROW_ID : conversationId} />
      </div>

      <div className={styles['chatPane']}>
        {notifications ? (
          <NotificationsScreen embedded />
        ) : conversationId ? (
          <ChatOrTopics key={conversationId} conversationId={conversationId} embedded />
        ) : (
          <div className={styles['placeholder']}>
            <span className={styles['placeholderPill']}>Выберите диалог, чтобы начать общение</span>
          </div>
        )}
      </div>
    </div>
  )
}

function ChatOrTopics({ conversationId, embedded = false }: { conversationId: string; embedded?: boolean }) {
  const params = useParams({ strict: false }) as { topicId?: string }
  const conversation = useConversation(conversationId)
  if (conversation?.channel?.format === 'group' && !params.topicId && conversation.channel.topics_enabled) {
    return <TopicListScreen conversationId={conversationId} embedded={embedded} />
  }
  return (
    <ChatScreen
      key={`${conversationId}:${params.topicId ?? ''}`}
      conversationIdOverride={conversationId}
      embedded={embedded}
    />
  )
}

/* ===================================================================== */
/* Список                                                                 */
/* ===================================================================== */

/** Долгое нажатие на строке, после которого открывается меню, а не диалог. */
const LONG_PRESS_MS = 450

function isMuted(conversation: Conversation): boolean {
  return conversation.my.muted_until !== null && Date.parse(conversation.my.muted_until) > Date.now()
}

function ConversationListPane({ archive, activeId = null }: { archive: boolean; activeId?: string | null }) {
  const navigate = useNavigate()
  const toast = useToast()
  const actions = useChatActions()
  const isDesktop = useIsDesktopLayout()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [dragged, setDragged] = useState<string | null>(null)
  const [reordering, setReordering] = useState(false)
  const reorderingRef = useRef(false)

  const { items: conversations, loaded } = useConversations(archive ? 'archived' : 'active')
  const foldersQuery = useChatFolders()
  const requestsQuery = useMessageRequests()
  const folders = foldersQuery.data ?? []
  const selectedFolder = folders.find((folder) => filter === `folder:${folder.id}`)

  // C-28: глобальный поиск по никнейму — от 5 символов алфавита никнейма, только вне архива.
  const config = useAppStore((s) => s.config)
  const rules = useMemo(() => usernameRules(config), [config])
  const globalQuery = archive ? null : usernameSearchQuery(query, rules)
  const globalSearch = useUsernameSearch(globalQuery)

  /* Черновики — как в Telegram: в строке видно, где остался неотправленный текст */
  const { items: drafts } = useReplicaList(() => draftsRepo.all(), [])
  const draftById = useMemo(() => {
    const map = new Map<string, string>()
    for (const draft of drafts) {
      const text = draft.text.trim()
      if (text) map.set(draft.conversation_id, text)
    }
    return map
  }, [drafts])
  const newMatches = useMatchesWithoutMessages()

  const visible = useMemo(
    () =>
      filterConversations(conversations, query)
        .filter((c) => c.request_role !== 'incoming')
        .filter(
          (c) =>
            filter === 'all' ||
            (selectedFolder !== undefined && inChatFolder(selectedFolder, c)) ||
            (filter === 'unread' && (c.unread_count > 0 || c.my.manual_unread)) ||
            (filter === 'direct' && c.kind !== 'channel') ||
            (filter === 'group' && c.channel?.format === 'group') ||
            (filter === 'channel' && c.channel?.format === 'channel'),
        )
        .sort(
          (a, b) =>
            (a.my.pinned_order ?? Number.MAX_SAFE_INTEGER) - (b.my.pinned_order ?? Number.MAX_SAFE_INTEGER) ||
            (a.my.pinned_order != null && b.my.pinned_order != null ? a.id.localeCompare(b.id) : 0),
        ),
    [conversations, query, filter, selectedFolder],
  )
  const openSaved = async () => {
    try {
      const saved = await ensureSavedConversation()
      if (!saved) {
        toast.show('Подключитесь к интернету, чтобы открыть Избранное в первый раз.')
        return
      }
      void navigate({ to: '/chats/$conversationId', params: { conversationId: saved.id } })
    } catch {
      toast.show('Не удалось открыть Избранное. Проверьте соединение.')
    }
  }
  const reorderPinned = async (source: string, target: string) => {
    if (reorderingRef.current) return
    reorderingRef.current = true
    setReordering(true)
    try {
      // Общий порядок включает диалоги за текущим фильтром и в архиве.
      const all = await conversationsRepo.list('all')
      for (const patch of reorderPinnedConversations(all, source, target)) {
        await actions.setPersonal(patch.conversationId, { pinned_order: patch.pinned_order })
      }
    } catch {
      toast.show('Не удалось изменить порядок закреплений. Попробуйте ещё раз.')
    } finally {
      reorderingRef.current = false
      setReordering(false)
    }
  }

  const publishVisibleRows = useCallback(
    (rows: readonly Conversation[]) => setVisibleConversations(rows.map((row) => row.id)),
    [],
  )

  useEffect(() => {
    if (visible.length <= VIRTUALIZE_THRESHOLD) publishVisibleRows(visible)
  }, [publishVisibleRows, visible])

  // Очищаем hint только при реальном unmount. Cleanup эффекта с
  // `visible` в dependencies срабатывал и при обычном rerender: для длинного
  // списка он затирал viewport, только что опубликованный VirtualList.
  useEffect(() => () => setVisibleConversations([]), [])

  // Меню строки (долгое нажатие / правая кнопка) — как в Telegram: действия без входа в диалог.
  const [rowMenu, setRowMenu] = useState<{ conversation: Conversation; anchor: DOMRect } | null>(null)
  const [muteFor, setMuteFor] = useState<Conversation | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)
  const [hideFor, setHideFor] = useState<Conversation | null>(null)
  const openRowMenu = useCallback(
    (conversation: Conversation, anchor: DOMRect) => setRowMenu({ conversation, anchor }),
    [],
  )
  const menuConversation = rowMenu?.conversation ?? null
  const menuFace = menuConversation ? faceOf(menuConversation) : null
  const menuUnavailable = menuFace ? menuFace.availability !== 'available' : true

  /* Свайп по строке (как в Telegram): «без звука» и «в архив»; длинный свайп сразу архивирует */
  const swipeActionsFor = useCallback(
    (conversation: Conversation): SwipeAction[] => {
      const muted = isMuted(conversation)
      const archived = conversation.my.archived_at !== null
      return [
        {
          key: 'mute',
          label: muted ? 'Звук' : 'Без звука',
          icon: muted ? 'bell' : 'bell-off',
          tone: 'warning',
          onSelect: () => {
            if (muted) void actions.setMuted(conversation.id, null)
            else setMuteFor(conversation)
          },
        },
        {
          key: 'archive',
          label: archived ? 'Из архива' : 'В архив',
          icon: 'archive',
          tone: 'accent',
          onSelect: () => void actions.setArchived(conversation.id, !archived),
        },
      ]
    },
    [actions],
  )

  return (
    <Screen
      title={archive ? 'Архив' : 'Чаты'}
      back={archive}
      flush
      actions={
        !archive ? (
          <>
            {/* Карандаш как в Telegram: канал, контакт или быстрый переход к переписке */}
            <Button
              icon="edit"
              variant="ghost"
              aria-label="Новое сообщение"
              onClick={() => setComposeOpen(true)}
            />
            <Button
              icon="archive"
              variant="ghost"
              aria-label="Архив"
              onClick={() => void navigate({ to: '/chats/archive' })}
            />
            {conversations.length >= 3 || folders.length > 0 ? (
              <Button
                variant="ghost"
                aria-label="Настроить папки"
                onClick={() => void navigate({ to: '/chats/folders' })}
              >
                Папки
              </Button>
            ) : null}
          </>
        ) : undefined
      }
      subbar={
        <div className={styles['searchWrap']}>
          <SearchInput
            value={query}
            onValueChange={setQuery}
            placeholder="Поиск"
            aria-label="Поиск по чатам"
            {...{ [CHAT_SEARCH_ATTR]: true }}
            onClear={() => setQuery('')}
          />
        </div>
      }
    >
      <div className={roadmap['tabs']} role="tablist" aria-label="Фильтр чатов">
        {[
          ['all', 'Все'],
          ['unread', 'Непрочитанные'],
          ['direct', 'Личные'],
          ['group', 'Группы'],
          ['channel', 'Каналы'],
        ].map(([id, label]) => (
          <button
            type="button"
            key={id}
            role="tab"
            aria-selected={filter === id}
            onClick={() => setFilter(id ?? 'all')}
          >
            {label}
          </button>
        ))}
        {!archive
          ? folders.map((folder) => {
              const count = folder.unread_conversations
              return (
                <button
                  type="button"
                  key={folder.id}
                  role="tab"
                  aria-selected={filter === `folder:${folder.id}`}
                  onClick={() => setFilter(`folder:${folder.id}`)}
                >
                  {folder.name}
                  {count > 0 ? ` · ${count}` : ''}
                </button>
              )
            })
          : null}
      </div>
      {!archive && requestsQuery.data?.length ? (
        <button
          type="button"
          className={styles['row']}
          onClick={() => void navigate({ to: '/chats/requests' })}
        >
          <Icon name="chats" size={28} />
          <span className={styles['rowName']}>Запросы на переписку · {requestsQuery.data.length}</span>
        </button>
      ) : null}
      {!archive ? (
        <button type="button" className={styles['row']} onClick={() => void openSaved()}>
          <Icon name="pushpin" size={28} />
          <span className={styles['rowName']}>Избранное</span>
        </button>
      ) : null}
      {query ? (
        <Button
          variant="ghost"
          icon="search"
          onClick={() => void navigate({ to: '/search', search: { q: query } })}
        >
          Поиск сообщений по всем чатам
        </Button>
      ) : null}
      {visible.filter((c) => c.my.pinned_order != null).length > 1 ? (
        <div className={roadmap['tabs']} aria-label="Порядок закреплённых чатов">
          {visible
            .filter((c) => c.my.pinned_order != null)
            .map((c, index, array) => (
              <button
                type="button"
                key={c.id}
                draggable={!reordering}
                disabled={reordering}
                onDragStart={() => setDragged(c.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragged) void reorderPinned(dragged, c.id)
                  setDragged(null)
                }}
                onClick={() => {
                  const previous = array[index - 1]
                  if (previous) void reorderPinned(c.id, previous.id)
                }}
                aria-label={`Переместить ${faceOf(c).name} выше`}
                title="Перетащите для изменения порядка; нажмите, чтобы поднять"
              >
                {index + 1}. {faceOf(c).name} ↑
              </button>
            ))}
        </div>
      ) : null}
      {/* Новые совпадения без переписки — отдельная секция над списком, вход в диалог одним касанием */}
      {!archive && !query && newMatches.length ? (
        <section className={styles['newMatchesSection']} aria-labelledby="chats-new-matches">
          <div className={styles['newMatchesHead']}>
            <h2 id="chats-new-matches" className={styles['newMatchesTitle']}>
              Новые совпадения
              <span className={styles['newMatchesCount']}>{newMatches.length}</span>
            </h2>
            <Link to="/contacts" className={styles['newMatchesAll']}>
              Все
              <Icon name="chevron-right" size={16} />
            </Link>
          </div>

          <div className={styles['newMatches']}>
            {newMatches.map((match) => (
              <Link
                key={match.id}
                to="/chats/$conversationId"
                params={{ conversationId: match.conversation_id }}
                className={styles['newMatch']}
                aria-label={`Написать: ${match.counterpart.display_name ?? 'новое совпадение'}`}
              >
                <span className={cx(styles['storyRing'], !match.seen && styles['storyRingNew'])}>
                  <span className={styles['storyInner']}>
                    <Avatar
                      name={match.counterpart.display_name}
                      photo={match.counterpart.photos[0] ?? null}
                      availability={match.counterpart.availability}
                      size="xl"
                    />
                  </span>
                </span>
                <span className={styles['newMatchName']}>{match.counterpart.display_name ?? '—'}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Служебный диалог «Движ» закреплён над списком — как канал сервиса в Telegram */}
      {!archive && matchesService(query) ? <ServiceRow active={activeId === SERVICE_ROW_ID} /> : null}

      {visible.length ? (
        // D-02: `↑`/`↓` ходят по строкам, `Enter` открывает — штатным поведением ссылки.
        <div
          className={cx(styles['list'], visible.length > VIRTUALIZE_THRESHOLD && styles['listFill'])}
          {...{ [KEY_LIST_ATTR]: true }}
        >
          {/* §11.2 п. 1: длинный список виртуализируется, короткий — нет */}
          {visible.length > VIRTUALIZE_THRESHOLD ? (
            <VirtualList
              items={visible}
              getKey={(conversation) => conversation.id}
              estimateSize={CONVERSATION_ROW_HEIGHT}
              className={styles['virtualScroller']}
              rememberScroll
              onVisibleItemsChange={publishVisibleRows}
            >
              {(conversation, index) => (
                <SwipeableRow
                  enabled={!isDesktop}
                  divider={index > 0}
                  actions={swipeActionsFor(conversation)}
                  onFullSwipe={() =>
                    void actions.setArchived(conversation.id, conversation.my.archived_at === null)
                  }
                >
                  <ConversationRow
                    conversation={conversation}
                    active={conversation.id === activeId}
                    draft={draftById.get(conversation.id) ?? null}
                    onMenu={openRowMenu}
                  />
                </SwipeableRow>
              )}
            </VirtualList>
          ) : (
            visible.map((conversation, index) => (
              <SwipeableRow
                key={conversation.id}
                enabled={!isDesktop}
                divider={index > 0}
                actions={swipeActionsFor(conversation)}
                onFullSwipe={() =>
                  void actions.setArchived(conversation.id, conversation.my.archived_at === null)
                }
              >
                <ConversationRow
                  conversation={conversation}
                  active={conversation.id === activeId}
                  draft={draftById.get(conversation.id) ?? null}
                  onMenu={openRowMenu}
                />
              </SwipeableRow>
            ))
          )}
        </div>
      ) : null}

      {/* C-28: люди по никнейму — отдельной секцией под локальными результатами */}
      {globalQuery ? (
        <section className={styles['globalSection']} aria-labelledby="chats-global-search">
          <h2 id="chats-global-search" className={styles['globalTitle']}>
            Глобальный поиск
          </h2>
          {globalSearch.items.length ? (
            <div className={styles['list']}>
              {globalSearch.items.map((item) => (
                <GlobalUserRow key={item.id} item={item} query={globalQuery} />
              ))}
            </div>
          ) : globalSearch.loading ? (
            <div className={styles['list']} aria-busy="true" aria-label="Ищем людей по никнейму">
              {[0, 1, 2].map((index) => (
                <div key={index} className={cx(styles['row'], styles['skeletonRow'])} aria-hidden>
                  <span className={styles['skeletonAvatar']} />
                  <span className={styles['rowBody']}>
                    <span className={styles['skeletonLine']} style={{ width: `${56 - index * 10}%` }} />
                    <span
                      className={cx(styles['skeletonLine'], styles['skeletonLineThin'])}
                      style={{ width: `${38 + index * 8}%` }}
                    />
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles['globalHint']}>Никого с никнеймом на «{globalQuery}» не нашлось</p>
          )}
        </section>
      ) : null}

      {!visible.length && !globalQuery && loaded ? (
        <ListEmptyState
          archive={archive}
          searching={Boolean(query)}
          onGoToPeople={() => void navigate({ to: '/dvizh' })}
        />
      ) : null}

      {/* Лист «Новое сообщение» тянется отдельным чанком: он нужен только по нажатию карандаша */}
      {composeOpen ? (
        <Suspense fallback={null}>
          <NewMessageSheet open onClose={() => setComposeOpen(false)} />
        </Suspense>
      ) : null}

      {/* Меню строки диалога */}
      <Sheet
        open={rowMenu !== null}
        onClose={() => setRowMenu(null)}
        anchorRect={rowMenu?.anchor ?? null}
        title={menuFace?.name ?? 'Диалог'}
        flush
      >
        <ActionList>
          {menuConversation ? (
            <>
              <ActionItem
                icon="pin"
                label={menuConversation.my.pinned_order != null ? 'Открепить чат' : 'Закрепить чат'}
                onClick={() => {
                  const target = menuConversation
                  setRowMenu(null)
                  void actions.setPersonal(target.id, {
                    pinned_order:
                      target.my.pinned_order != null
                        ? null
                        : Math.max(-1, ...conversations.map((c) => c.my.pinned_order ?? -1)) + 1,
                  })
                }}
              />
              <ActionItem
                icon="chats"
                label={
                  menuConversation.my.manual_unread
                    ? 'Снять отметку непрочитанного'
                    : 'Отметить непрочитанным'
                }
                onClick={() => {
                  const target = menuConversation
                  setRowMenu(null)
                  void actions.setPersonal(target.id, { manual_unread: !target.my.manual_unread })
                }}
              />
            </>
          ) : null}
          {menuConversation && menuConversation.unread_count > 0 ? (
            <ActionItem
              icon="check"
              label="Прочитано"
              onClick={() => {
                const target = menuConversation
                setRowMenu(null)
                void actions.markRead(target.id, target.last_seq)
              }}
            />
          ) : null}
          <ActionItem
            icon={menuFace?.channel ? spaceIcon(menuFace.channel.format) : 'user'}
            label={
              menuFace?.channel
                ? menuFace.channel.format === 'group'
                  ? 'О группе'
                  : 'О канале'
                : 'Открыть профиль'
            }
            disabled={menuUnavailable}
            onClick={() => {
              const target = menuConversation
              const channel = menuFace?.channel
              setRowMenu(null)
              if (channel) {
                void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
              } else if (target?.counterpart) {
                void navigate({ to: '/users/$userId', params: { userId: target.counterpart.id } })
              }
            }}
          />
          <ActionItem
            icon="bell-off"
            label={
              menuConversation && isMuted(menuConversation) ? 'Включить уведомления' : 'Отключить уведомления'
            }
            onClick={() => {
              const target = menuConversation
              setRowMenu(null)
              if (!target) return
              if (isMuted(target)) void actions.setMuted(target.id, null)
              else setMuteFor(target)
            }}
          />
          <ActionItem
            icon="archive"
            label={menuConversation?.my.archived_at ? 'Убрать из архива' : 'В архив'}
            onClick={() => {
              const target = menuConversation
              setRowMenu(null)
              if (target) void actions.setArchived(target.id, target.my.archived_at === null)
            }}
          />
          <ActionItem
            icon="trash"
            label="Удалить чат у меня"
            danger
            onClick={() => {
              const target = menuConversation
              setRowMenu(null)
              setHideFor(target)
            }}
          />
        </ActionList>
      </Sheet>

      {/* C-06: длительность mute */}
      <Sheet
        open={muteFor !== null}
        onClose={() => setMuteFor(null)}
        title="Отключить уведомления"
        description="Счётчик непрочитанного останется — mute влияет только на push."
        flush
      >
        <ActionList>
          {MUTE_OPTIONS.map((option) => (
            <ActionItem
              key={option.label}
              icon="bell-off"
              label={option.label}
              onClick={() => {
                const target = muteFor
                setMuteFor(null)
                if (target) void actions.setMuted(target.id, new Date(Date.now() + option.ms).toISOString())
              }}
            />
          ))}
        </ActionList>
      </Sheet>

      {/* C-24: подтверждение удаления */}
      <Dialog
        open={hideFor !== null}
        onClose={() => setHideFor(null)}
        title="Удалить чат у себя?"
        description="Диалог исчезнет только у вас и вернётся с новым сообщением."
        footer={
          <>
            <Button variant="secondary" onClick={() => setHideFor(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = hideFor
                setHideFor(null)
                if (target) {
                  void actions
                    .hideConversation(target.id)
                    .catch(() => toast.show('Не удалось удалить чат', { tone: 'danger' }))
                }
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </Screen>
  )
}

/** Клиентский поиск по имени собеседника (D-30). */
function filterConversations(items: readonly Conversation[], query: string): Conversation[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...items]

  return items.filter((item) => (faceOf(item).name ?? '').toLowerCase().includes(needle))
}

/* ===================================================================== */
/* Служебный диалог «Движ» (N-01)                                          */
/* ===================================================================== */

const SERVICE_NAME = 'Движ'

/** Строка находится и по имени сервиса, и по слову «уведомления». */
function matchesService(query: string): boolean {
  const q = query.trim().toLowerCase()
  return !q || SERVICE_NAME.toLowerCase().includes(q) || 'уведомления'.includes(q)
}

function ServiceRow({ active }: { active: boolean }) {
  const unread = useUnreadNotificationsCount()
  const { items: notifications } = useNotifications()
  // Реплика отдаёт свежие первыми (BS §13.1)
  const latest = notifications[0] ?? null
  const preview = latest ? describeNotification(latest).title : 'Совпадения, сообщения и события аккаунта'

  return (
    <Link
      to="/notifications"
      className={cx(styles['row'], styles['serviceRow'], active && styles['rowActive'])}
      aria-label={`${SERVICE_NAME}: уведомления`}
      aria-current={active ? 'page' : undefined}
      {...{ [KEY_ITEM_ATTR]: true }}
    >
      <span className={styles['serviceAvatar']} aria-hidden>
        <LogoTile size={52} />
      </span>

      <span className={styles['rowBody']}>
        <span className={styles['rowTop']}>
          <span className={styles['rowName']}>
            <span className={styles['rowNameText']}>{SERVICE_NAME}</span>
            <span className={styles['serviceMark']} title="Официальный сервис">
              <Icon name="check" size={10} />
            </span>
          </span>
          {latest ? <span className={styles['rowTime']}>{formatListTime(latest.created_at)}</span> : null}
        </span>

        <span className={styles['rowBottom']}>
          <span className={cx(styles['rowPreview'], unread > 0 && styles['rowPreviewUnread'])}>
            {preview}
          </span>
          <span className={styles['rowMarks']}>
            {unread > 0 ? (
              <span className={styles['rowBadge']}>
                <Badge count={unread} />
              </span>
            ) : null}
          </span>
        </span>
      </span>
    </Link>
  )
}

/* ===================================================================== */
/* C-28: строка глобального поиска по никнейму                            */
/* ===================================================================== */

function GlobalUserRow({ item, query }: { item: UserSearchResult; query: string }) {
  const name = item.display_name ?? 'Пользователь'
  // Совпавшее начало никнейма выделено — так видно, почему строка нашлась (как в Telegram)
  const matched = item.username.toLowerCase().startsWith(query.toLowerCase()) ? query.length : 0
  return (
    <Link
      to="/users/$userId"
      params={{ userId: item.id }}
      className={styles['row']}
      aria-label={`${name}, @${item.username}`}
      {...{ [KEY_ITEM_ATTR]: true }}
    >
      <Avatar name={item.display_name} photo={item.photo} availability="available" size="lg" />
      <div className={styles['rowBody']}>
        <div className={styles['rowTop']}>
          <span className={styles['rowName']}>
            <span className={styles['rowNameText']}>
              {name}
              {item.age !== null ? `, ${item.age}` : ''}
            </span>
          </span>
        </div>
        <div className={styles['rowBottom']}>
          <span className={styles['rowPreview']}>
            @<span className={styles['matched']}>{item.username.slice(0, matched)}</span>
            {item.username.slice(matched)}
            {item.city ? ` · ${item.city.name}` : ''}
          </span>
        </div>
      </div>
    </Link>
  )
}

/* ===================================================================== */
/* Строка диалога                                                         */
/* ===================================================================== */

function ConversationRow({
  conversation,
  active,
  draft,
  onMenu,
}: {
  conversation: Conversation
  active: boolean
  /** Неотправленный текст композера — показывается вместо превью, пока диалог не открыт. */
  draft: string | null
  onMenu: (conversation: Conversation, anchor: DOMRect) => void
}) {
  const myUserId = useAppStore((s) => s.me?.user.id ?? '')
  const counterpart = conversation.counterpart
  // Изменение v1.7: строкой списка может быть лента канала — у неё нет контрагента.
  const face = faceOf(conversation)
  const channel = face.channel
  const unavailable = face.availability !== 'available'
  const last = conversation.last_message
  const muted = isMuted(conversation)
  // «печатает…» прямо в списке, как в Telegram
  const typing = useTyping(conversation.id)

  /*
   * Долгое нажатие открывает меню строки, а не диалог: таймер стартует по
   * касанию, сбрасывается движением пальца или отпусканием; клик после
   * сработавшего таймера гасится. Правая кнопка на desktop — то же меню.
   */
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pressStart = useRef<{ x: number; y: number } | null>(null)
  const longPressed = useRef(false)

  const cancelPress = (): void => {
    if (pressTimer.current) clearTimeout(pressTimer.current)
    pressTimer.current = null
    pressStart.current = null
  }

  return (
    <Link
      to="/chats/$conversationId"
      params={{ conversationId: conversation.id }}
      className={cx(
        styles['row'],
        active && styles['rowActive'],
        conversation.unread_count > 0 && !muted && styles['rowUnread'],
      )}
      aria-current={active ? 'page' : undefined}
      aria-label={rowLabel(conversation, face.name)}
      {...{ [KEY_ITEM_ATTR]: true }}
      onContextMenu={(event) => {
        // На Android долгое нажатие по ссылке открыло бы меню браузера — гасим его.
        event.preventDefault()
        if (longPressed.current) return
        onMenu(conversation, event.currentTarget.getBoundingClientRect())
      }}
      onPointerDown={(event) => {
        if (event.pointerType !== 'touch') return
        longPressed.current = false
        pressStart.current = { x: event.clientX, y: event.clientY }
        const target = event.currentTarget
        pressTimer.current = setTimeout(() => {
          pressTimer.current = null
          longPressed.current = true
          onMenu(conversation, target.getBoundingClientRect())
        }, LONG_PRESS_MS)
      }}
      onPointerMove={(event) => {
        const start = pressStart.current
        if (!start || !pressTimer.current) return
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) cancelPress()
      }}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      // Свайп строки (SwipeableRow) захватывает указатель — строка получает pointerleave и не открывает меню
      onPointerLeave={cancelPress}
      onClick={(event) => {
        if (!longPressed.current) return
        event.preventDefault()
        longPressed.current = false
      }}
    >
      <Avatar
        name={face.name}
        photo={face.photo}
        availability={face.availability}
        size="lg"
        online={counterpart?.presence?.online ?? false}
      />

      <div className={styles['rowBody']}>
        <div className={styles['rowTop']}>
          <span className={styles['rowName']}>
            {channel ? (
              <span
                className={styles['rowNameMark']}
                aria-label={channel.format === 'group' ? 'Группа' : 'Канал'}
              >
                <Icon name={spaceIcon(channel.format)} size={14} />
              </span>
            ) : null}
            <span className={styles['rowNameText']}>
              {/* ПС §10: недоступный профиль — нейтрально, без раскрытия причины */}
              {channel
                ? channel.title
                : unavailable
                  ? face.availability === 'deleted'
                    ? 'Удалённый аккаунт'
                    : 'Профиль недоступен'
                  : face.name}
            </span>
            {muted ? (
              <span className={styles['rowNameMark']} aria-label="Уведомления отключены">
                <Icon name="bell-off" size={14} />
              </span>
            ) : null}
          </span>

          {last ? (
            <span className={styles['rowTime']}>
              <MessageReceipt conversation={conversation} />
              {formatListTime(last.created_at)}
            </span>
          ) : null}
        </div>

        <div className={styles['rowBottom']}>
          <span
            className={cx(styles['rowPreview'], conversation.unread_count > 0 && styles['rowPreviewUnread'])}
          >
            {typing ? (
              <span className={styles['typing']}>печатает…</span>
            ) : draft && !active ? (
              <>
                <span className={styles['rowDraft']}>Черновик: </span>
                {draft}
              </>
            ) : (
              <>
                {/*
                 * Кто написал: своё сообщение — «Вы:», чужое в группе — именем автора
                 * (изменение v1.10, как в Telegram). У переписки автор назван заголовком
                 * строки, у канала посты идут от самого канала — там подписи нет.
                 */}
                {conversation.last_message && !conversation.last_message.deleted ? (
                  conversation.last_message.sender_id === myUserId ? (
                    <span className={styles['rowPreviewMe']}>Вы: </span>
                  ) : conversation.last_message.sender_name ? (
                    <span className={styles['rowPreviewMe']}>{conversation.last_message.sender_name}: </span>
                  ) : null
                ) : null}
                {previewText(conversation)}
              </>
            )}
          </span>

          <span className={styles['rowMarks']}>
            {conversation.my.pinned_order != null ? <Icon name="pin" size={15} /> : null}
            {conversation.my.manual_unread && !conversation.unread_count ? (
              <span aria-label="Отмечено непрочитанным">●</span>
            ) : null}
            {conversation.my.archived_at ? <Icon name="archive" size={15} /> : null}
            {conversation.state === 'closed' ? <span className={styles['closedMark']}>закрыт</span> : null}
            {conversation.unread_count > 0 ? (
              <span className={styles['rowBadge']}>
                <Badge count={conversation.unread_count} muted={muted} />
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </Link>
  )
}

/** Доступное имя строки: имя, число непрочитанных и признак «без звука» — без чтения всей вёрстки. */
function rowLabel(conversation: Conversation, name: string | null): string {
  const parts = [name ?? 'Диалог']
  if (conversation.unread_count > 0) {
    parts.push(`${conversation.unread_count} ${pluralUnread(conversation.unread_count)}`)
  }
  if (conversation.my.manual_unread) parts.push('отмечено непрочитанным')
  if (isMuted(conversation)) parts.push('уведомления отключены')
  return parts.join(', ')
}

function pluralUnread(count: number): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'непрочитанное'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'непрочитанных'
  return 'непрочитанных'
}

function previewText(conversation: Conversation): string {
  const last = conversation.last_message
  if (!last) {
    /*
     * Пустая лента канала или группы — не «совпадение»: там никакого match нет, и строка
     * обещала бы собеседника, которого не существует (изменение v1.10).
     */
    if (conversation.kind === 'channel') {
      return conversation.channel?.format === 'group' ? 'Пока нет сообщений' : 'Пока нет публикаций'
    }
    if (conversation.kind === 'saved') return 'Личные заметки и сохранённые сообщения'
    return 'Совпадение — напишите первым'
  }
  if (last.deleted) return 'Сообщение удалено'

  switch (last.type) {
    case 'file':
      return 'Файл'
    case 'image':
      return 'Фотография'
    case 'video':
      return 'Видео'
    case 'voice':
      return 'Голосовое сообщение'
    default:
      return last.preview ?? ''
  }
}

/* ===================================================================== */
/* Пустые состояния (ПС §19)                                              */
/* ===================================================================== */

/** C-04 «Пустой список чатов» и C-27 «Пустой архив / пустой поиск». */
function ListEmptyState({
  archive,
  searching,
  onGoToPeople,
}: {
  archive: boolean
  searching: boolean
  onGoToPeople: () => void
}) {
  if (searching) {
    return <EmptyState icon="search" title="Ничего не нашлось" text="Попробуйте другое имя." />
  }

  if (archive) {
    return <EmptyState icon="archive" title="Архив пуст" text="Сюда попадают диалоги, убранные из списка." />
  }

  return (
    <EmptyState
      icon="chats"
      title="Пока нет диалогов"
      text="Здесь появятся переписки с теми, с кем у вас взаимное «Го»."
      actions={
        <Button variant="primary" onClick={onGoToPeople}>
          Открыть «Движ»
        </Button>
      }
    />
  )
}
